import { Box, Button, Grid, Stack } from '@mui/material';
import React from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import PolicyholderApi from '../../api/PolicyholderApi';
import { useAuthContext } from '../../context/auth';
import { useConfigContext } from '../../context/config';
import { useErrorContext } from '../../context/ErrorProvider';
import { getCreatePolicyholderAutogeneratedReq } from '../../util/util';
import { FastForm, useFastForm } from '../fastForm';
import { FORM_NAME } from '../fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getPolicyholderForm,
} from '../fastForm/formBuilders';
import { policyholderUpdateBuilder } from '../fastForm/updateBuilders';
import LargeCenterSpinner from '../LargeCenterSpinner';
import { errorToast, successToast } from '../Toast';
import { PolicyholderLoadingForm } from './PolicyholderLoadingForm';

const CreatePolicyholder = () => {
	const navigate = useNavigate();
	const authData = useAuthContext();
	const configData = useConfigContext();
	const errorData = useErrorContext();
	const authToken = authData.auth.authorizationToken;

	const form = getPolicyholderForm({ configData, entity: {} });
	const defaultValues = getDefaultPolicyValues(form, authData);
	const methods = useFastForm(form, defaultValues) as UseFormReturn<
		FieldValues,
		object
	>;

	const [submitting, setSubmitting] = React.useState(false);
	const { getValues, handleSubmit } = methods;
	const { dirtyFields } = methods.formState;

	const submit = async () => {
		try {
			setSubmitting(true);

			const newPolicyholderRequest: any = getCreatePolicyholderAutogeneratedReq(
				configData.createPolicyholderWorkflow,
			);

			const newPolicyholder = await PolicyholderApi.createPolicyholder(
				authData.auth.authorizationToken,
				newPolicyholderRequest,
			);

			const updatePolicyholderRequest = policyholderUpdateBuilder(
				dirtyFields,
				getValues(),
				form,
				FORM_NAME.policyholder,
				newPolicyholder.data.locator,
				newPolicyholder.data.version,
				authToken,
			);

			const policyholder = await PolicyholderApi.updatePolicyholder(
				authData.auth.authorizationToken,
				updatePolicyholderRequest,
			);

			successToast('Successfully created policyholder');
			navigate(`/policyholder/${policyholder.data.locator}`);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to initialize policyholder');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setSubmitting(false);
	};

	const cancel = () => {
		// go back
		navigate(-1);
	};

	const handleErrors = () => {
		errorToast('Cannot submit, there are errors on the form.');
	};

	if (
		!defaultValues ||
		!configData.policyholderCreateFields ||
		Object.keys(defaultValues).length === 0
	) {
		return <PolicyholderLoadingForm />;
	}

	return (
		<FormProvider {...methods}>
			<Box
				component='form'
				noValidate
				onSubmit={(e: React.FormEvent) => handleSubmit(submit, handleErrors)(e)}
			>
				{form && !submitting ? (
					<Grid container spacing={2}>
						<FastForm
							formName={FORM_NAME.policyholder}
							form={form}
							configVersion=''
							productName=''
							authData={authData}
							disabled={submitting}
						/>
						<Grid item xs={12}>
							<Stack direction='row' spacing={2} justifyContent='flex-end'>
								<Button color='primary' variant='text' onClick={cancel}>
									Cancel
								</Button>
								<Button color='primary' variant='contained' type='submit'>
									Create
								</Button>
							</Stack>
						</Grid>
					</Grid>
				) : (
					<LargeCenterSpinner />
				)}
			</Box>
		</FormProvider>
	);
};

export default CreatePolicyholder;
